// src/hooks/useConnections.ts

import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosContext } from '../contextManager/axios.context';
import { endpoints } from '../constants/endpoints';
import { useUser } from '../contextManager/user.context';
import { useToast } from '../contextManager/toast.context';

// You'll need to define this type based on your DB table
export interface Connection {
  id: string;
  parentId: string;
  tutorId: string;
  status: 'saved' | 'contacted' | 'archived';
  created_at: string;
  tutor: ConnectedUser;
  parent: ConnectedUser; // <-- ADD THIS PROPERTY
  lastMessageAt: string | null;
  lastMessageContent: string | null;
  lastMessageSenderId: string | null;
  hasUnreadMessages: boolean;
}

interface ConnectedUser {
  id: string;
  name: string;
  role: 'parent' | 'tutor';
  profilePhotoUrl?: string;
  profileColor?: string;
  subjects?: string[];
  hourlyRate?: number;
  timezone?: string;
  availability?: import('../types/schemas/abc').AvailabilityObject;
  offersFreeIntroCall?: boolean;
}

export const useConnections = () => {
  const axios = useContext(AxiosContext);
  const { userDetails } = useUser();
  const { addToast, updateToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['connections', userDetails?.id];

  const { data: connections = [], isLoading } = useQuery<Connection[]>({
    queryKey: queryKey,
    enabled: !!userDetails?.id, // Only run the query if the user is logged in
    queryFn: async () => {
      // You will need a new endpoint for this in your constants/backend
      const response = await axios.get(endpoints.myConnections); 
      return response.data;
    },
  });


  const deleteConnections = useMutation({
    mutationFn: (connectionIds: string[]) => {
      return axios.delete(endpoints.myConnections, { data: { ids: connectionIds } });
    },
    onMutate: (connectionIds) => {
      const toastId = `connection-delete-${Date.now()}`;
      const title = connectionIds.length > 1 ? `Deleting ${connectionIds.length} connections...` : 'Deleting connection...';
      addToast({ id: toastId, variant: 'loading', title });
      return { toastId };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({ queryKey: queryKey });
      if (context) updateToast(context.toastId, { variant: 'success', title: 'Connections deleted.' });
    },
    onError: (_, __, context) => {
      if (context) updateToast(context.toastId, { variant: 'error', title: 'Failed to delete connections.' });
    }
  }).mutateAsync;


  const createParentConnection = useMutation({
    mutationFn: (newConnection: {tutorId: string; status: 'saved' | 'contacted' }) => {
      return axios.post(`${endpoints.myConnections}/for-parent`, newConnection);
    },
    onMutate: (variables) => {
      const toastId = `connection-create-${Date.now()}`;
      const title = variables.status === 'contacted' ? 'Saving and contacting tutor...' : 'Saving tutor...';
      addToast({ id: toastId, variant: 'loading', title });
      return { toastId };
    },
    onSuccess: (response, _, context) => {
      if (!context) return;
      if (response.data.message === 'Connection already exists.') {
        updateToast(context.toastId, { variant: 'info', title: 'Connection already exists.' });
        return;
      }
      queryClient.invalidateQueries({ queryKey: queryKey });
      updateToast(context.toastId, { variant: 'success', title: 'Tutor saved!' });
    },
    onError: (_, __, context) => {
      if (context) updateToast(context.toastId, { variant: 'error', title: 'Failed to save tutor.' });
    }
  }).mutateAsync;


  const createTutorConnection = useMutation({
    mutationFn: (newConnection: {parentId: string; status: 'saved' | 'contacted' }) => {
      return axios.post(`${endpoints.myConnections}/for-tutor`, newConnection);
    },
    onMutate: (variables) => {
      const toastId = `connection-create-${Date.now()}`;
      const title = variables.status === 'contacted' ? 'Saving and contacting parent...' : 'Saving parent...';
      addToast({ id: toastId, variant: 'loading', title });
      return { toastId };
    },
    onSuccess: (response, _, context) => {
      if (!context) return;
      if (response.data.message === 'Connection already exists.') {
        updateToast(context.toastId, { variant: 'info', title: 'Connection already exists.' });
        return;
      }
      queryClient.invalidateQueries({ queryKey: queryKey });
      updateToast(context.toastId, { variant: 'success', title: 'Parent saved!' });
    },
    onError: (_, __, context) => {
      if (context) updateToast(context.toastId, { variant: 'error', title: 'Failed to save parent.' });
    }
  }).mutateAsync;


  const createConnection = useMutation({
      mutationFn: (newConnection: { otherUserId: string; status: 'saved' | 'contacted' }) => {
        return axios.post(endpoints.myConnections, newConnection);
      },
      onMutate: () => {
        const toastId = `connection-create-${Date.now()}`;
        addToast({ id: toastId, variant: 'loading', title: 'Saving connection...' });
        return { toastId };
      },
      onSuccess: (response, _, context) => {
        if (!context) return;
        if (response.data.message === 'Connection already exists.') {
          updateToast(context.toastId, { variant: 'info', title: 'Connection already exists.' });
          return;
        }
        queryClient.invalidateQueries({ queryKey: queryKey });
        updateToast(context.toastId, { variant: 'success', title: 'Tutor saved!' });
      },
      onError: (_, __, context) => {
        if (context) updateToast(context.toastId, { variant: 'error', title: 'Failed to save tutor.' });
      }
    }).mutateAsync;

    const updateConnection = useMutation({
      mutationFn: (variables: { connectionId: string; newStatus: Connection['status'] }) => {
        const { connectionId, newStatus } = variables;
        return axios.patch(endpoints.updateConnection(connectionId), { status: newStatus });
      },
      onMutate: () => {
        const toastId = `connection-update-${Date.now()}`;
        addToast({ id: toastId, variant: 'loading', title: 'Updating connection...' });
        return { toastId };
      },
      onSuccess: (_, __, context) => {
        queryClient.invalidateQueries({ queryKey: queryKey });
        if (context) updateToast(context.toastId, { variant: 'success', title: 'Connection updated.' });
      },
      onError: (_, __, context) => {
        if (context) updateToast(context.toastId, { variant: 'error', title: 'Failed to update connection.' });
      },
    }).mutateAsync;

    return {
      connections,
      isLoading,
      createConnection,
      createParentConnection, 
      createTutorConnection,
      updateConnection,
      deleteConnections,
    };
};